import wikiPage from './wiki-page.png'
import wikipageClass from './wikipage-class.png'

const content = `<p>The Command design pattern consists in creating objects that represent actions. Actions can then be stored, loaded, applied at a later time, unapplied, reapplied, sorted, and many other things, in code that is separate from the objects they interact with.</p>

<p>Commands are <a href="/pure-fabrication">Pure fabrications</a> that don’t map to domain objects, as actions are rather verbs than objects in the domain.</p>

<p>By grouping all the code of an action inside of a class and by separating the actions from the object they are performed on, Command can help to achieve <a href="/high-cohesion">High cohesion</a> in the code.</p>

<p>Let’s take an example to illustrate.</p>

<h2 id="Case-study">Case study</h2>

<p>A company has an internal wiki where the author of a page can tag teams that could be interested in the page. The members of those teams receive email notifications whenever the page is updated.</p>

<img src=${wikiPage} alt="wiki-page" />

<p>When the above page gets updated, the system checks who is in the alpha team and who is in the bravo team, and sends them an email notification.</p>

<p>However, individual users can choose to unsubscribe from the notification of a page, or to subscribe to it even if they’re not in the tagged teams.</p>

<p>How do we design the code that computes the list of users to notify when a page is updated?</p>

<h2 id="Structuring-code-by-action-types">Structuring code by action types</h2>

<p>Before seeing the command pattern, let’s have a look at an alternative design that may come to mind first (it did in the project that inspired this example).</p>

<p>One way to structure the code is to have a list of tagged teams, a list of unsubscribed users and a list of subscribed users. Another class, <code>MailingLists</code>, provides the list of emails of the members of a team:</p>

<pre><code class="language-ruby">class WikiPage
  def emails_to_notify
    tagged_teams_emails =
      tagged_teams.flat_map{ |team| MailingLists.get_emails_from_team(team) }
    
    subscribed_emails = SubscribedEmail.where(page: self)
    unsubscribed_emails = UnsubscribedEmail.where(page: self)
    tagged_teams_emails + subscribed_emails - unsubscribed_emails
  end
  
  private
  
  def tagged_teams
    # code that extracts the tagged teams from the page contents
  end
end</code></pre>

<p>The data model contains a table storing manual subscriptions (accessed by <code>SubscribedEmail</code>):</p>

<table><tbody><tr><th><strong>id</strong></th><th><strong>wiki_page_id</strong></th><th><strong>email</strong></th></tr><tr><td>1</td><td>42</td><td>alice@company.com</td></tr><tr><td>2</td><td>42</td><td>bob@company.com</td></tr></tbody></table>

<p>And a table storing manual unsubscriptions (accessed by <code>UnsubscribedEmail</code>):</p>

<table><tbody><tr><th><strong>id</strong></th><th><strong>wiki_page_id</strong></th><th><strong>email</strong></th></tr><tr><td>1</td><td>42</td><td>john@company.com</td></tr></tbody></table>

<p>With this design, the <code>WikiPage</code> accumulates several concerns:</p>

<img src=${wikipageClass} alt="wikipage-class" />

<p>Some of them are strongly related to the wiki page, and other are less related. The least related concerns are arguably the subscriptions and unsubscriptions, as these concepts are very specific to the notifications associated to the page, and they’re not so much part of the page itself.</p>

<p>Moreover, those concerns could grow in size if the notification systems gets more user actions. Some users could opt for batch notifications every day or every week, some would prefer to have them in real time, and so on.</p>

<p>The Command pattern offers an alternative to structuring the code by action type: extracting code in individual actions.</p>

<h2 id="The-Command-pattern">The Command pattern</h2>

<p>The Command pattern consists in extracting the code of the actions into separate objects. The <code>WikiPage</code> knows there are various actions, but doesn’t know them individually.</p>

<p>The data model is different than in the previous design. Now there is only one table that stores all the actions:</p>

<table><tbody><tr><th><strong>id</strong></th><th><strong>wiki_page_id</strong></th><th><strong>label</strong></th><th><strong>email</strong></th></tr><tr><td>1</td><td>42</td><td>subscribe</td><td>alice@company.com</td></tr><tr><td>2</td><td>42</td><td>unsubscribe</td><td>john@company.com</td></tr><tr><td>3</td><td>42</td><td>subscribe</td><td>bob@company.com</td></tr></tbody></table>

<p>The point is to extract from <code>WikiPage</code> the code that performs specific actions. In an example so small as this one, where actions consists of single short expressions like (<code>email_list + [@email]</code>), we can consider conflating that code that performs actions with the model, as it’s convenient. The entire code then becomes:</p>

<pre><code class="language-ruby">class NotificationAction &lt; ApplicationRecord
  def apply(email_list)
    case label
    when 'subscribe' then email_list + &#91;email]
    when 'unsubscribe' then email_list - &#91;email]
    end
  end
end

class WikiPage
  def emails_to_notify
    emails = tagged_teams.flat_map{ |team| MailingLists.get_emails_from_team(team) }
    
    NotificationAction.where(wiki_page: self).reduce(emails) do |emails, action|
      action.apply(emails)
    end
  end
  
  private
  
  def tagged_teams
    # code that extracts the tagged teams from the page contents
  end
end</code></pre>

<p>With so little code it’s reasonable to stop here. But it’s important to note that we can separate the actions further to keep the cohesion of <code>NotificationAction</code> if there is more code in it.</p>

<h3 id="Separating-the-actions-from-the-model">Separating the actions from the model</h3>

<p>To separate the actions from the model and from one another, we can create a class for each action. Here is the <code>SubscribeEmail</code> class:</p>

<pre><code class="language-ruby">class SubscribeEmail
  def initialize(email)
    @email = email
  end
  
  def apply(email_list)
    email_list + &#91;@email]
  end
end</code></pre>

<p>And here is the <code>UnsubscribedEmail</code> class:</p>

<pre><code class="language-ruby">class UnsubscribeEmail
  def initialize(email)
    @email = email
  end
  
  def apply(email_list)
    email_list - &#91;@email]
  end
end</code></pre>

<p>The model can map the action labels to the action classes:</p>

<pre><code class="language-ruby">class NotificationAction &lt; ApplicationRecord
  def self.for_wiki_page(wiki_page_id)
    NotificationAction
      .where(wiki_page_id: wiki_page_id)
      .map do |action|
        case action.label
        when 'subscribe' then SubscribeEmail.new(action.email)
        when 'unsubscribe' then UnsubscribeEmail.new(action.email)
        end
    end
  end
end</code></pre>

<p>And the <code>WikiPage</code> is very similar to the previous implementation:</p>

<pre><code class="language-ruby">class WikiPage
  def emails_to_notify
    emails = tagged_teams.flat_map{ |team| MailingLists.get_emails_from_team(team) }
    
    NotificationAction.for_wiki_page(id).reduce(emails) do |emails, action|
      action.apply(emails)
    }
  end
  
  private
  
  def tagged_teams
    # code that extracts the tagged teams from the page contents
  end
end</code></pre>

<h2 id="When-to-apply-the-pattern">When to apply the pattern</h2>

<p>In this example, applying the Command pattern has advantages and drawbacks.</p>

<p>Its main drawback in this code is that it adds a bunch of code to a piece of code that was simple, especially with the second version. When is the Command pattern improving the design and when is it over-engineering?</p>

<p>Let’s examine the advantages of the new code.</p>

<p><strong>The data model is simpler</strong>. There is one table instead of two, and new actions of the same type would not require to change the data model.</p>

<p><strong>The order of actions is preserved</strong>. If a user unsubscribes and then subscribes, with the first design they won’t get the notifications, as the all the unsubscriptions are applied after all the subscriptions.</p>

<p><strong>Adding a new action doesn’t change the code of </strong><code>WikiPage</code><strong>. </strong>Adding new actions requires to add an action class and an entry in the <code>case</code> statement to map its label. Said differently, <code>WikiPage</code> is not coupled with the actions.</p>

<p>In a piece of code as simple as our example, where the action is one short line of code (<code>email_list + [@email]</code>), applying a Command pattern is overkill if you don’t value the above advantages.</p>

<p>However, even if it’s convenient to take a simple example like this one to illustrate how the Command pattern works, in practice code is often more complicated and prone to change.</p>

<p>Adding a bit of code to increase the cohesion of classes becomes more valuable when there is more code involved. The better cohesion and decoupling that the pattern brings makes large amounts of code easier to change and to reason about.</p>

<div class="notes">
<p>Key points</p>

<ul><li>The Command pattern extracts the various actions in dedicated objects</li><li>This decouples the actions and the objects the action are performed on</li></ul>
</div>`

export default content
